import { Stack, Region } from 'contentstack';
import Agent from 'agentkeepalive';
import { INCLUDE_FALLBACK_DEFAULT_VALUE } from '../consts/fallback';
const commonKeepAliveAgentOptions = {
    keepAlive: true,
    maxSockets: 75,
    maxFreeSockets: 25,
    timeout: 60000,
    freeSocketTimeout: 30000,
};
export class ContentstackService {
    constructor(config) {
        config.region = config.region ?? Region.EU;
        this.stack = Stack(config);
        this.stack.fetchOptions.agent = new Agent.HttpsAgent(commonKeepAliveAgentOptions);
    }
    injectAttributesToRetrieve(query, attributesToRetrieve = []) {
        if (attributesToRetrieve.length > 0) {
            attributesToRetrieve.forEach((a) => {
                query.only(a);
            });
        }
    }
    applyOrderBy(query, orderByParams) {
        if (orderByParams.length > 0) {
            orderByParams.forEach((order) => {
                const { field, isDesc } = order;
                if (field) {
                    isDesc ? query.descending(field) : query.ascending(field);
                }
            });
        }
    }
    getStack() {
        return this.stack;
    }
    getContentType(uuid) {
        return this.stack.ContentType(uuid);
    }
    async getContent({ uuid, languageCode, findQuery = {}, reference = [], onError, orderBy = [], attributesToRetrieve, includeFallback = INCLUDE_FALLBACK_DEFAULT_VALUE, }) {
        const language = languageCode.toLocaleLowerCase();
        try {
            const query = this.stack
                .ContentType(uuid)
                .Query()
                .query(findQuery)
                .includeReference(reference)
                .language(language);
            if (includeFallback) {
                query.includeFallback();
            }
            query.toJSON();
            this.applyOrderBy(query, orderBy);
            this.injectAttributesToRetrieve(query, attributesToRetrieve);
            const response = await query.find();
            return response[0];
        }
        catch (catchError) {
            onError?.(catchError);
            return [];
        }
    }
    async getSingletonContent({ uuid, languageCode, findQuery = {}, reference = [], onError, orderBy = [], attributesToRetrieve, includeFallback = INCLUDE_FALLBACK_DEFAULT_VALUE, }) {
        const language = languageCode.toLocaleLowerCase();
        try {
            const query = this.stack
                .ContentType(uuid)
                .Query()
                .query(findQuery)
                .includeReference(reference)
                .language(language);
            if (includeFallback) {
                query.includeFallback();
            }
            query.toJSON();
            this.applyOrderBy(query, orderBy);
            this.injectAttributesToRetrieve(query, attributesToRetrieve);
            const response = await query.findOne();
            return response;
        }
        catch (catchError) {
            onError?.(catchError);
            return {};
        }
    }
    async getLatestContentTypesModificationDate({ languageCode, listOfCtToCheck, findQuery = {}, contentTypeUidReferences = {}, attributesToRetrieve, }) {
        const language = languageCode.toLocaleLowerCase();
        const latestCtEntryUpdate = new Map();
        for (const ct of listOfCtToCheck) {
            try {
                const uidReferences = contentTypeUidReferences[ct];
                if (uidReferences) {
                    const contentTypeUids = [ct, ...uidReferences];
                    const response = await this.getUiReferencesLatestModifiedDate(contentTypeUids, language, findQuery, attributesToRetrieve);
                    latestCtEntryUpdate.set(ct, {
                        data: response.referencesMap[ct],
                        lastModified: response.lastModified,
                    });
                }
                else {
                    const item = await this.getLatestContentTypeUpdatedEntry({
                        uuid: ct,
                        languageCode: language,
                        findQuery,
                        attributesToRetrieve,
                    });
                    latestCtEntryUpdate.set(ct, { data: item, lastModified: item?.updated_at });
                }
            }
            catch {
                latestCtEntryUpdate.set(ct, {
                    data: undefined,
                });
            }
        }
        return latestCtEntryUpdate;
    }
    async getLatestContentTypeUpdatedEntry({ uuid, languageCode, findQuery = {}, orderBy = [], onError, attributesToRetrieve = [], }) {
        try {
            const query = this.getStack()
                .ContentType(uuid)
                .Query()
                .query(findQuery)
                .descending('updated_at')
                .language(languageCode)
                .toJSON();
            this.applyOrderBy(query, orderBy);
            this.injectAttributesToRetrieve(query, attributesToRetrieve);
            const response = await query.findOne();
            return response;
        }
        catch (catchError) {
            onError?.(catchError);
            return {};
        }
    }
    async getUiReferencesLatestModifiedDate(contentTypeUids, language, findQuery = {}, attributesToRetrieve) {
        let latestIsoDate;
        const referencesMap = {};
        // Traverse all possible references for the parent and get latest modified date
        for (const ctId of contentTypeUids) {
            const item = await this.getLatestContentTypeUpdatedEntry({
                uuid: ctId,
                languageCode: language,
                findQuery,
                attributesToRetrieve,
            });
            referencesMap[ctId] = item;
            // compare all dates including parent and take latest one
            if (!latestIsoDate || new Date(item.updated_at) > new Date(latestIsoDate)) {
                latestIsoDate = item.updated_at;
            }
        }
        return {
            referencesMap: referencesMap,
            lastModified: latestIsoDate,
        };
    }
}
//# sourceMappingURL=contentstack-service.js.map